import { Columns3, List, CalendarDays, ChartGantt, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import TaskOrganizationToolbar from '@/frontend/features/Fde/TaskOrganizationToolbar'
import type {
  ManagementProps,
  ProjectManagementView,
  ProjectTaskFilters,
  ProjectTaskFilter,
} from '@/frontend/features/Fde/managementTypes'
import type { ProjectTaskAssignee } from '@/frontend/features/Fde/types'

interface Props {
  props: ManagementProps
  view: ProjectManagementView
  onView: (value: ProjectManagementView) => void
  filters: ProjectTaskFilters
  onFilters: (value: ProjectTaskFilters) => void
  assignees: ProjectTaskAssignee[]
  attentionCount: number
  onNew: () => void
  groupBy: 'status' | 'category'
  onGroup: (value: 'status' | 'category') => void
  showChildren: boolean
  onChildren: (value: boolean) => void
}

export default function ProjectManagementToolbar({
  props,
  view,
  onView,
  filters,
  onFilters,
  assignees,
  attentionCount,
  onNew,
  groupBy,
  onGroup,
  showChildren,
  onChildren,
}: Props) {
  const copy = props.copy.management
  const views = [
    { key: 'board' as const, label: copy.board_view, icon: Columns3 },
    { key: 'list' as const, label: copy.list_view, icon: List },
    { key: 'schedule' as const, label: copy.schedule_view, icon: CalendarDays },
    { key: 'gantt' as const, label: copy.gantt_view, icon: ChartGantt },
  ]
  const scopes: Array<{ key: ProjectTaskFilter; label: string; count?: number }> = [
    { key: 'all', label: copy.all_tasks },
    { key: 'open', label: copy.open_filter },
    { key: 'attention', label: copy.attention_filter, count: attentionCount },
    { key: 'done', label: copy.completed_filter },
  ]
  return (
    <div className="shrink-0 border-b border-border bg-background px-4 lg:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3 py-3">
        <div role="group" aria-label={copy.tasks} className="flex rounded-lg bg-muted/60 p-1">
          {views.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              aria-pressed={view === key}
              aria-label={label}
              title={label}
              onClick={() => onView(key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
                view === key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {(view === 'board' || view === 'list') && (
            <TaskOrganizationToolbar
              props={props}
              groupBy={groupBy}
              onGroup={onGroup}
              showChildren={showChildren}
              onChildren={onChildren}
              allowChildrenToggle={view === 'board'}
            />
          )}
          {props.can_manage && view !== 'summary' && (
            <Button size="sm" onClick={onNew}>
              <Plus className="size-3.5" />
              {copy.add_task}
            </Button>
          )}
        </div>
      </div>
      {view !== 'summary' && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pb-3">
          <div role="group" aria-label={copy.task_status} className="flex flex-wrap gap-1">
            {scopes.map(({ key, label, count }) => (
              <button
                type="button"
                key={key}
                aria-pressed={filters.status === key}
                onClick={() => onFilters({ query: '', assignee: '', status: key })}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
                  filters.status === key
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
                {Boolean(count) && (
                  <span
                    className={cn(
                      'rounded px-1 text-[10px] tabular-nums',
                      filters.status === key
                        ? 'bg-background/20'
                        : 'bg-amber-500/15 text-amber-700',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:flex-1 sm:max-w-sm">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label={view === 'schedule' ? copy.search_calendar : copy.search_tasks}
                placeholder={view === 'schedule' ? copy.search_calendar : copy.search_tasks}
                value={filters.query}
                onChange={(event) => onFilters({ ...filters, query: event.target.value })}
                className="h-8 bg-muted/30 pl-8 text-xs"
              />
            </div>
            <FdeSelect
              value={filters.assignee}
              ariaLabel={copy.filter_assignee}
              onValueChange={(assignee) => onFilters({ ...filters, assignee })}
              options={[
                { value: '', label: copy.filter_assignee },
                ...assignees.map((item) => ({ value: item.value, label: item.name })),
              ]}
              size="sm"
              className="h-8 w-40 text-xs"
            />
            {(filters.query || filters.assignee) && (
              <Button
                size="icon-sm"
                variant="ghost"
                aria-label={copy.clear_filters}
                onClick={() => onFilters({ ...filters, query: '', assignee: '' })}
              >
                <X className="size-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
